import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Link
} from '@mui/material';
import customerAuthService from '@/service/auth.service/customer.auth.service';
import { useLocation, useNavigate } from 'react-router-dom';

export default function VerifyForgetPassword() {
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const email = localStorage.getItem('email');
  useEffect(() => {
    const storedEmail = location.state?.email || localStorage.getItem('email');
    console.log('Stored email:', typeof storedEmail);
    if(storedEmail === null|| storedEmail === 'undefined') {
        navigate('/customer/auth/forget-password');
    }
    else{
        localStorage.setItem('email', storedEmail);
        setOtp('');
        setError('');
    }
  }, [location.state, navigate]);
  
  // Countdown Timer
  useEffect(() => {
    let timer;
    if (!canResend && countdown > 0) {
      timer = setTimeout(() => setCountdown(prev => prev - 1), 1000);
    } else if (countdown === 0) {
      setCanResend(true);
    }
    return () => clearTimeout(timer);
  }, [countdown, canResend]);

  const handleOtpChange = (e) => {
    const input = e.target.value;
    if (/^\d{0,5}$/.test(input)) {
      setOtp(input);
      setError('');
      setSuccessMessage('');
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    if (!/^\d{5}$/.test(otp)) {
      setError('OTP must be a 5-digit number.');
      setLoading(false);
      return;
    }

    try {
      const response = await customerAuthService.verifyForgetPasswordOtp(email, otp);
      if (response?.status) {
        setSuccessMessage('OTP verified successfully.');
        localStorage.removeItem('email');
        setTimeout(() => navigate('/customer/auth/reset-password', { state: { email } }), 2000);
      } else {
        setError('Invalid OTP. Please try again.');
      }
    } catch (error) {
      console.log(error);
      const message = error?.response?.data?.data?.error || 'An error occurred. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setCanResend(false);
    setCountdown(30);
    setSuccessMessage('');
    setError('');
    setLoading(true);

    try {
      const response = await customerAuthService.forgotPassword(email);
      if (response?.status) {
        setSuccessMessage('A new OTP has been sent to your email.');
      } else {
        setError('Failed to resend OTP. Try again later.');
        setCanResend(true);
      }
    } catch (err) {
      setError('Error while resending OTP.');
      setCanResend(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper
      className="inner-card"
      elevation={5}
      sx={{ p: 4, maxWidth: 400, mx: 'auto', mt: 4 }}
    >
      <Typography variant="h5" color="#5E5ACF" fontWeight="bold" gutterBottom>
        Reset Password OTP
      </Typography>

      <Typography variant="body2" sx={{ mb: 2 }}>
        A one-time password has been sent to:{' '}
        <Typography
          component="span"
          sx={{ fontWeight: 'bold', color: '#6a00f4', fontSize: '0.9rem' }}
        >
          {email}
        </Typography>
        .
      </Typography>

      <form onSubmit={handleOtpSubmit}>
        <TextField
          fullWidth
          margin="normal"
          label="Enter 5-digit OTP"
          variant="outlined"
          value={otp}
          onChange={handleOtpChange}
          error={!!error}
          helperText={error}
          inputProps={{ maxLength: 5 }}
        />

        {successMessage && (
          <Typography sx={{ color: 'green', fontSize: '0.875rem', mt: 1 }}>
            {successMessage}
          </Typography>
        )}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          className="btn-submit"
          sx={{ mt: 2, color: 'black', fontWeight: 'bold' }}
          disabled={loading || otp.length !== 5}
        >
          {loading ? <CircularProgress size={24} sx={{ color: 'black' }} /> : 'Verify OTP'}
        </Button>
      </form>

      <div style={{ textAlign: 'center', marginTop: '16px' }}>
        {!canResend ? (
          <Typography variant="body2" color="gray">
            You can resend OTP in {countdown} seconds
          </Typography>
        ) : (
          <Typography variant="body2">
            <Link
              href="#"
              onClick={handleResendOtp}
              sx={{
                cursor: 'pointer',
                textDecoration: 'none',
                color: '#5E5ACF',
                fontWeight: 'bold',
              }}
            >
              Resend OTP
            </Link>
          </Typography>
        )}
      </div>
    </Paper>
  );
}
